/* Sixteen <video> elements render on the homepage, across the hero, how-to,
   taste, the pricing cards and the shelf panels. They all used to carry
   `autoplay`, so every one began decoding the moment it entered the DOM, on
   screen or not, and every mp4 came down in full on every page load. On
   Mornings and Nights each section drops a panel so it survives. On Combo
   every section renders and a phone decodes all sixteen at once. That was
   the "lag / glitch in combo".

   Pausing them was not enough: `autoplay` overrides `preload="none"`, so the
   files still downloaded. So the ten non-hero films carry `data-silt-film`
   and no `autoplay`, and nothing about them loads until they are near the
   viewport. The three hero films keep `autoplay`, because the hero is the
   first paint and must not wait on JavaScript.

   Measured on a 375 load: three mp4s requested instead of twelve, and those
   three as partial content rather than in full.

   FROM THE TOP, EVERY TIME (her 6 Oct: "all the loops should start from the
   start ALWAYS, even when someone scrolls up and down, and also from a
   different page to this one"). Three rules do it:
   1. A film is BUFFERED one screen early but PLAYED only once it is seen
      (30% of it, or 30% of the screen for a film taller than that). Playing
      it one screen early meant you arrived a second or two into the loop.
   2. Any loop that goes fully off screen is paused and rewound there, out of
      sight, so coming back shows its first frame and never a jump.
   3. Leaving the page (another page, another tab, the back button's cached
      copy) rewinds every loop; coming back plays the ones on screen from
      the top.
   A film can name its own first frame with data-silt-start (the hero does on
   phones, 1.5s). Sections that run their own pair (how-to) or their own
   trigger (the phone box films) carry data-silt-own-play: this file never
   plays those, but still rewinds them.

   TWO WAYS IN, ON PURPOSE. IntersectionObserver drives this, but with the
   films' own autoplay removed, a browser where the observer never fires
   would show ten dead posters. That is a worse bug than the one this fixes,
   so a throttled scroll handler runs the same check independently. Whichever
   gets there first wins; start() is idempotent. */
(() => {
  /* 11 Oct PageSpeed: everything below is boot(root). It runs once for the
     page, as before, and again (window.siltVideoBoot) for each homepage
     section unpacked on a Mornings or Nights tap (snippets/silt-later.liquid).
     Each run takes only the videos no earlier run has taken. */
  const boot = (root) => {
  /* Every loop on the page, for the rewind. A video with controls is one a
     visitor drives (product gallery), so it is left alone. */
  const loops = [...root.querySelectorAll('video:not([controls])')].filter((v) => !v.__siltManaged);
  if (!loops.length) return;
  loops.forEach((v) => { v.__siltManaged = 1; });

  /* PHONE COPIES (7 Oct audit). These films were encoded for laptop width
     but show at 250-345 px on a phone. Each has a <name>-phone.mp4 beside it
     at three times its phone width (35-55% fewer bytes, VMAF 89-90 against
     the laptop file). On a phone the
     source is swapped before anything has been fetched (preload none, empty
     network state), so the big file is never requested. One list, here,
     rather than a phone <source> in every section that renders a film. */
  const PHONE_COPY = /\/(silt-(?:tq-push4-pm|tq-push5-am|tq-push6-am|tq-push6-pm|tq-orig-am|tq-orig-pm|tq-5oct-am|tq-5oct-pm|howto-am-wide-v3|howto-pm-wide-v5|card-am))\.mp4/;
  if (window.matchMedia && matchMedia('(max-width: 749px)').matches) {
    loops.forEach((v) => {
      /* preload none has already picked its source (network state idle) but
         fetched nothing (readyState 0). No load() here: Chrome on Android
         fetches on load() even with preload none (the 6 Oct AV1 lesson).
         Every loader, this file's warm() and the sections' own, calls load()
         before the first play, and that load() picks the phone copy. */
      if (v.readyState !== 0 || v.preload !== 'none') return;
      const own = v.getAttribute('src') || '';
      if (PHONE_COPY.test(own)) v.setAttribute('src', own.replace(PHONE_COPY, '/$1-phone.mp4'));
      v.querySelectorAll('source').forEach((s) => {
        const src = s.getAttribute('src') || '';
        if (!s.media && PHONE_COPY.test(src)) s.setAttribute('src', src.replace(PHONE_COPY, '/$1-phone.mp4'));
      });
    });
  }
  const films = loops.filter((v) => v.hasAttribute('autoplay') || v.hasAttribute('data-silt-film'));

  /* POSTERS ON APPROACH (6 Oct audit). A poster attribute is fetched the
     moment the page is parsed, wherever its film sits: a phone on the Combo
     homepage pulled 900 KB of posters on first load, for films 7,500 px down
     and for the ones hidden in the other two toggles, while the hero was
     still loading. So every film except the hero's carries data-silt-poster,
     and it becomes the poster once the film is within two and a half screens
     below or one and a half above. Measured from the box rather than by an
     observer, because the review cards sit in a sideways rail an observer
     would call clipped until each card is swiped in. */
  let undressed = [...root.querySelectorAll('video[data-silt-poster]')].filter((v) => !v.__siltPosterSeen);
  undressed.forEach((v) => { v.__siltPosterSeen = 1; });
  const dressOne = (v) => {
    if (!v.getAttribute('poster') && v.dataset.siltPoster) v.setAttribute('poster', v.dataset.siltPoster);
  };
  const dress = () => {
    if (!undressed.length) return;
    const vh = window.innerHeight;
    undressed = undressed.filter((v) => {
      const r = v.getBoundingClientRect();
      if (!r.width && !r.height) return true;      // display:none, another toggle's film
      if (r.top > vh * 2.5 || r.bottom < -vh * 1.5) return true;
      dressOne(v);
      return false;
    });
  };
  dress();

  const MARGIN = () => window.innerHeight;
  const startOf = (v) => parseFloat(v.dataset.siltStart) || 0;
  const ownPlay = (v) => v.hasAttribute('data-silt-own-play') || v.dataset.siltOwnPlay === '1';

  /* Armed = the next time it is seen it starts from its first frame. Every
     loop starts armed, except one already playing on screen when this runs
     (the hero, autoplaying since first paint): that one the visitor is
     watching begin right now. */
  const armed = new WeakMap();

  /* SLOW CONNECTIONS (7 Oct audit, Android only: iOS has no
     navigator.connection). On 3G, 2G or with Data Saver on, nothing is
     buffered ahead of the visitor, and a film that comes into view stays on
     its poster until the browser says it can play through, instead of
     starting and stalling every few seconds. */
  const conn = navigator.connection;
  const slowNet = () => !!conn && (!!conn.saveData || /2g|3g/.test(conn.effectiveType || ''));

  /* DOWNLOAD QUEUE (7 Oct audit). Buffering every film within a screen meant
     up to ten downloads at once on the Combo page, all sharing one slow line,
     so the one you reach first arrived last. Now at most two buffer AHEAD of
     the visitor at a time, nearest first; a film you are looking at always
     starts at once, cap or not. One left far behind before it was ever
     played is stopped, so it stops eating the line. */
  const loading = new Set();
  const settle = (film) => {
    clearTimeout(film.__siltLoadTimer);
    if (loading.delete(film)) setTimeout(pump, 0);
  };
  const track = (film) => {
    if (loading.has(film)) return;
    loading.add(film);
    const EV = ['canplaythrough', 'error', 'suspend', 'playing'];
    const done = () => {
      EV.forEach((e) => film.removeEventListener(e, onEv));
      settle(film);
    };
    /* suspend = the browser stopped fetching on its own; it only counts once
       there is enough to keep going, or it would free the slot too early.
       playing = it is on screen now, so it is no longer buffering AHEAD; the
       slot goes to the next film down (sections that start their own films
       never pass through start() here, so this is how those let go). */
    const onEv = (e) => { if (e.type !== 'suspend' || film.readyState >= 3) done(); };
    EV.forEach((e) => film.addEventListener(e, onEv));
    /* A stuck download must not block the queue forever. */
    film.__siltLoadTimer = setTimeout(done, 12000);
  };

  const warm = (film, ahead) => {
    dressOne(film);
    if (film.dataset.siltStarted === '1') return true;
    if (ahead && loading.size >= (slowNet() ? 0 : 2)) return false;
    film.dataset.siltStarted = '1';
    /* preload="none" means nothing is buffered yet, so ask for it before
       the first play or the element sits on a blank frame. */
    if (film.preload === 'none') {
      film.preload = 'auto';
      film.load();
      track(film);
    }
    return true;
  };

  /* Stop a download that is no longer worth having: started ahead, never
     played, and now more than three screens below or two above. Back to
     preload none and load() aborts the fetch; it rejoins the queue if the
     visitor comes back towards it. Never the hero, which autoplays. */
  const shelve = (film) => {
    if (!loading.has(film) || film.hasAttribute('autoplay') || film.dataset.siltPlayed === '1') return;
    if (!film.paused || (film.played && film.played.length)) return;
    const r = film.getBoundingClientRect();
    const vh = window.innerHeight;
    if (r.top < vh * 3 && r.bottom > -vh * 2 && (r.width || r.height)) return;
    settle(film);
    film.preload = 'none';
    try { film.load(); } catch (e) {}
    film.dataset.siltStarted = '';
  };

  /* Two screens ahead, not one: with at most two downloading there is no
     pile-up to fear, and on slow 4G a 450 KB loop needs the extra screen of
     scrolling to arrive ready. */
  const ahead = (f) => {
    const r = f.getBoundingClientRect();
    if (!r.width && !r.height) return false;
    if (!shown(f)) return false;
    const vh = window.innerHeight;
    return r.bottom > -vh && r.top < vh * 3;
  };
  const queue = () => films.filter((f) => f.dataset.siltStarted !== '1' && ahead(f));
  let pumping = false;
  function pump() {
    if (pumping) return;
    pumping = true;
    loading.forEach(shelve);
    const vh = window.innerHeight;
    const dist = (f) => {
      const r = f.getBoundingClientRect();
      return r.top > vh ? r.top - vh : r.bottom < 0 ? -r.bottom : 0;
    };
    queue().sort((a, b) => dist(a) - dist(b)).some((f) => !warm(f, true));
    pumping = false;
  }

  const start = (film) => {
    /* Slow line: buffer it now, but play only once it can play through. */
    if (slowNet() && film.readyState < 4 && !film.hasAttribute('autoplay')) {
      warm(film);
      if (!film.__siltWaitThrough) {
        film.__siltWaitThrough = true;
        film.addEventListener('canplaythrough', () => {
          film.__siltWaitThrough = false;
          if (seen(film)) { toTop(film); start(film); }
        }, { once: true });
      }
      return;
    }
    warm(film);
    film.dataset.siltPlayed = '1';
    armed.set(film, false);
    if (!film.paused) return;
    const p = film.play();
    /* play() rejects if the element is paused again before the promise
       settles, which is normal on a fast scroll. */
    if (p && p.catch) p.catch(() => {});
  };

  /* Back to the first frame. Skipped when it is already within a quarter
     second of it, so a film that has only just begun is not made to stutter. */
  const toTop = (v, force) => {
    const s = startOf(v);
    if (!force && Math.abs(v.currentTime - s) < 0.25) return;
    try { v.currentTime = s; } catch (e) {}
  };

  const rewind = (v) => {
    if (!v.paused) v.pause();
    armed.set(v, true);
    if (Math.abs(v.currentTime - startOf(v)) > 0.04) toTop(v, true);
  };

  /* The hero crossfades its three states with `opacity: 0`, keeping each one
     at full size and in flow. So a rect test says all three are on screen and
     every one of them starts. checkVisibility catches that; where it is
     missing, walk the ancestors for the same thing. */
  const shown = (el) => {
    if (typeof el.checkVisibility === 'function') {
      return el.checkVisibility({ opacityProperty: true, visibilityProperty: true });
    }
    let n = el;
    while (n && n.nodeType === 1) {
      const c = getComputedStyle(n);
      if (c.display === 'none' || c.visibility === 'hidden' || Number(c.opacity) === 0) return false;
      n = n.parentElement;
    }
    return true;
  };

  const near = (film) => {
    const r = film.getBoundingClientRect();
    if (!r.width && !r.height) return false;      // display:none, the off-mode panel
    if (!shown(film)) return false;               // the faded-out hero states
    const m = MARGIN();
    return r.bottom > -m && r.top < window.innerHeight + m;
  };

  /* How much of it is on screen, from a rect (the observer hands over its
     own, which also counts a carousel clipping it sideways). */
  const enough = (vis, r) =>
    vis.width > 0 && vis.height > 0 && vis.height >= Math.min(r.height * 0.3, window.innerHeight * 0.3);

  const onScreen = (r) => ({
    width: Math.min(r.right, window.innerWidth) - Math.max(r.left, 0),
    height: Math.min(r.bottom, window.innerHeight) - Math.max(r.top, 0)
  });

  const seen = (v) => {
    const r = v.getBoundingClientRect();
    if (!r.width && !r.height) return false;
    return enough(onScreen(r), r) && shown(v);
  };

  const gone = (v) => {
    const r = v.getBoundingClientRect();
    if (!r.width && !r.height) return true;
    const vis = onScreen(r);
    return vis.width <= 0 || vis.height <= 0;
  };

  /* SYNC GROUPS. The Combo shelf section runs two films side by side, one
     per panel, and they must wipe at the same instant. Started independently
     they begin whenever each one happens to be ready and then drift apart
     over repeated loops, which reads as one panel lagging the other.

     So films sharing a data-silt-sync value are started in the same tick from
     a common zero, and the first of them drives the rest: on every timeupdate
     any follower more than 80ms out is pulled back to the leader. 80ms is
     about two frames at 30fps, under what the eye reads as a split. */
  const groups = {};
  films.forEach((film) => {
    const g = film.dataset.siltSync;
    if (g) (groups[g] = groups[g] || []).push(film);
  });

  Object.values(groups).forEach((members) => {
    if (members.length < 2) return;
    const leader = members[0];
    leader.addEventListener('timeupdate', () => {
      if (leader.paused) return;
      members.slice(1).forEach((f) => {
        if (f.paused || !f.duration) return;
        if (Math.abs(f.currentTime - leader.currentTime) > 0.08) {
          f.currentTime = leader.currentTime;
        }
      });
    });
  });

  /* Starting one member starts the whole group, and only once EVERY member
     can play through. That wait is the point: started as each one happens to
     be ready, the panel that buffers first is already part way through its
     wipe when the other begins, and the two never line up again. Buffer both,
     then start both in the same tick from the same zero. */
  const startGroup = (film, fromTop) => {
    const g = film.dataset.siltSync;
    /* Only members on the page right now: a mode that hides two of the three
       pricing cards must not wait on, or download, the hidden films. */
    const members = g && groups[g] && groups[g].filter((f) => f.getClientRects().length);
    if (!members || members.length < 2) {
      if (fromTop) toTop(film);
      return start(film);
    }

    /* Already kicked off, so this is a RETURN VISIT: scrolled away, paused,
       scrolled back. Resume every member rather than bailing out. Bailing was
       a bug: the pair played once and then sat frozen on the second visit.
       From the top, all of them together, when the visit is a new one. */
    if (members.some((f) => f.dataset.siltGroupStarted === '1')) {
      if (fromTop && members.some((f) => Math.abs(f.currentTime - startOf(f)) >= 0.25)) {
        members.forEach((f) => toTop(f, true));
      }
      members.forEach(start);
      return;
    }

    const fire = () => {
      if (members.some((f) => f.dataset.siltGroupStarted === '1')) return;
      members.forEach((f) => {
        f.dataset.siltGroupStarted = '1';
        try { f.currentTime = startOf(f); } catch (e) {}
      });
      members.forEach(start);
    };

    /* readyState 3 is HAVE_FUTURE_DATA: enough to begin and keep going. */
    const pending = members.filter((f) => f.readyState < 3);
    if (!pending.length) return fire();

    members.forEach((f) => warm(f));
    members.forEach((f) => armed.set(f, false));

    let waiting = pending.length;
    pending.forEach((f) => {
      const onReady = () => {
        f.removeEventListener('canplay', onReady);
        if (--waiting === 0) fire();
      };
      f.addEventListener('canplay', onReady);
    });
    /* A film that never reaches canplay must not hold the other hostage. */
    setTimeout(fire, 3000);
  };

  /* One decision per loop, from whatever measured it. */
  const decide = (v, isSeen, isGone) => {
    if (isGone) {
      /* 7 Oct: a sync pair rewinds as one, and only once EVERY member has
         left the screen. Rewinding the half that scrolled off while the other
         kept playing is how a pair fell out of step. */
      const g = v.dataset.siltSync;
      const members = g && groups[g] && groups[g].filter((f) => f.getClientRects().length);
      if (members && members.length > 1) {
        if (members.every(gone)) members.forEach((f) => { rewind(f); delete f.dataset.siltGroupStarted; });
        return;
      }
      rewind(v); return;
    }
    if (!isSeen || ownPlay(v) || !films.includes(v)) return;
    if (armed.get(v) !== false) startGroup(v, true);
    else if (v.paused) startGroup(v, false);
  };

  loops.forEach((v) => {
    armed.set(v, !(v.hasAttribute('autoplay') && !v.paused && seen(v)));
  });

  /* The ten lazy films are paused up front so the checks below decide. THE
     HERO IS NOT. It carries `autoplay`, and pausing it the instant it starts
     means iOS treats it as a paused inline video and paints its own play
     glyph over the poster, which is what she photographed. Nothing restarts
     it either, in the one case that matters: with Low Power Mode on, iOS
     refuses both autoplay and a scripted play(), so the film stays under that
     button until something else starts it. */
  films.forEach((film) => { if (!film.hasAttribute('autoplay')) film.pause(); });

  if ('IntersectionObserver' in window) {
    /* Buffer one screen early... */
    const pre = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        /* Not the two hero states faded out behind the third: buffering
           them cost a phone 1.6 MB on first load for films it never shows
           (6 Oct audit). The toggle starts them by name when it is used. */
        if (entry.isIntersecting && shown(entry.target)) pump();
      });
    }, { rootMargin: '100% 0px' });
    films.forEach((film) => pre.observe(film));

    /* ...play once seen, rewind once gone. A film that is in place but still
       fading in (shown() false) is checked again a moment later, or a jump
       straight to it with no scroll after would leave it on its poster. */
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const v = entry.target;
        if (!entry.isIntersecting) return decide(v, false, true);
        const isSeen = enough(entry.intersectionRect, entry.boundingClientRect);
        decide(v, isSeen && shown(v), false);
        if (isSeen && !shown(v)) setTimeout(() => decide(v, seen(v), gone(v)), 900);
      });
    }, { threshold: [0, 0.05, 0.1, 0.15, 0.2, 0.25, 0.3, 0.4, 0.5] });
    loops.forEach((v) => io.observe(v));
  }

  const sweep = () => {
    dress();
    pump();
    loops.forEach((v) => decide(v, seen(v), gone(v)));
  };

  /* And that something is the first touch anywhere on the page. iOS allows
     play() inside a gesture handler even in Low Power Mode, so one listener
     clears the glyph the moment they touch, scroll or tap. It runs once and
     removes itself. */
  const kick = () => {
    films.forEach((film) => { if (!ownPlay(film) && seen(film)) startGroup(film, armed.get(film) !== false); });
    ['touchstart', 'pointerdown', 'keydown', 'scroll'].forEach((e) =>
      window.removeEventListener(e, kick, true));
  };
  ['touchstart', 'pointerdown', 'keydown', 'scroll'].forEach((e) =>
    window.addEventListener(e, kick, { capture: true, passive: true }));

  let ticking = false;
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    setTimeout(() => { ticking = false; sweep(); }, 200);
  };
  addEventListener('scroll', onScroll, { passive: true });
  addEventListener('resize', onScroll, { passive: true });

  /* LEAVING AND COMING BACK. Another tab, another app, or another page and
     then Back (the browser keeps this page in memory and shows it again as it
     was, mid-loop). Rewind everything on the way out, so the restored page
     paints first frames, and on the way back start what is on screen from
     the top, including the loops a section plays itself: its own observer
     never fires again for a page that did not move. A loop someone turned the
     sound on for is theirs, and is left alone. */
  let away = false;
  const leave = () => {
    if (away) return;
    away = true;
    loops.forEach((v) => {
      if (!v.muted) return;
      v.dataset.siltWasPlaying = v.paused ? '' : '1';
      rewind(v);
    });
  };
  const back = () => {
    if (!away) return;
    away = false;
    loops.forEach((v) => {
      if (!v.muted) return;
      const was = v.dataset.siltWasPlaying === '1';
      v.dataset.siltWasPlaying = '';
      if (!seen(v)) return;
      if (films.includes(v) && !ownPlay(v)) return startGroup(v, true);
      if (was) { const p = v.play(); if (p && p.catch) p.catch(() => {}); }
    });
  };
  document.addEventListener('visibilitychange', () => (document.hidden ? leave() : back()));
  addEventListener('pagehide', leave);
  addEventListener('pageshow', (e) => { if (e.persisted) back(); });

  /* A toggle switch is not a scroll, so the hero needs its own hook.

     It cannot go through seen(): the hero CROSSFADES, so the state being
     switched to is still at opacity 0 when the event fires, and the
     visibility test would correctly call it hidden and skip it. Measured: the
     film then did not start until some later sweep, which on a real switch
     means the hero sits on its poster and then jumps.

     So the incoming state is started by name rather than by looking at it,
     when the hero is on screen at all (the hero's own script has already put
     it back to its first frame). The delayed sweep afterwards pauses whatever
     faded out and starts the toggled films further down from the top. */
  document.addEventListener('drinksilt:themechange', () => {
    const mode = document.body.getAttribute('data-drinksilt-select') || 'both';
    document
      .querySelectorAll('[data-silt-state="' + mode + '"] video')
      .forEach((v) => { if (loops.includes(v) && !gone(v)) start(v); });
    requestAnimationFrame(dress);
    setTimeout(sweep, 700);
  });

  /* KEEP PLAYING (7 Oct, her "on phone every part of the videos should play
     and we should not need to refresh"). Everything above starts a film once,
     on the scroll or observer tick that sees it. When that one play() is
     refused (iPhone Low Power Mode refuses scripted play outside a touch) or
     the download errors or stalls on a patchy line, the film sat on its
     poster or a frozen frame until a refresh. Three nets:
     1. Every 1.5 s, each film on screen is checked. Paused when it should be
        playing: started (its pair too). Errored, or the same frame for three
        checks running: reloaded and resumed where it was, at most every 6 s.
     2. Every tap retries the paused films on screen (the first-touch kick
        above removes itself; iOS lets play() through inside any touch).
     3. A slow line no longer waits forever for "can play through": after
        10 s on screen it plays with what it has. */
  const health = new WeakMap();
  const recover = (f) => {
    const h = health.get(f) || {};
    const now = Date.now();
    if (h.fixAt && now - h.fixAt < 6000) return;
    h.fixAt = now; h.still = 0; health.set(f, h);
    const t = f.currentTime;
    f.preload = 'auto';
    f.addEventListener('loadedmetadata', () => {
      try { if (t > 0.1 && f.duration && t < f.duration - 0.2) f.currentTime = t; } catch (e) {}
      if (!ownPlay(f) && seen(f)) startGroup(f, false);
    }, { once: true });
    try { f.load(); } catch (e) {}
  };
  const watch = () => {
    if (document.hidden) return;
    films.forEach((f) => {
      if (!seen(f)) { health.delete(f); return; }
      const h = health.get(f) || { t: -1, still: 0, since: Date.now() };
      if (f.error || f.networkState === 3) { health.set(f, h); recover(f); return; }
      if (f.__siltWaitThrough && Date.now() - h.since > 10000) {
        f.__siltWaitThrough = false;
        f.dataset.siltForce = '1';
      }
      if (f.paused) {
        h.still = 0;
        if (!ownPlay(f) && !f.__siltWaitThrough) {
          if (f.dataset.siltForce === '1') { delete f.dataset.siltForce; const p = f.play(); if (p && p.catch) p.catch(() => {}); }
          else startGroup(f, false);
        }
      } else {
        h.still = Math.abs(f.currentTime - h.t) < 0.01 ? h.still + 1 : 0;
        if (h.still >= 3) recover(f);
      }
      h.t = f.currentTime;
      health.set(f, h);
    });
  };
  setInterval(watch, 1500);
  const retry = () => {
    films.forEach((f) => { if (!ownPlay(f) && f.paused && seen(f)) startGroup(f, false); });
  };
  ['touchend', 'click'].forEach((e) => window.addEventListener(e, retry, { capture: true, passive: true }));

  sweep();
  };
  boot(document);
  window.siltVideoBoot = boot;
})();
